import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/AdminNavigation';
import NavigationBreadcrumbs from '../../components/NavigationBreadcrumbs';
import { useAuth } from '../../components/AuthenticationBoundary';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabaseClient';
import TermsEditor from './components/TermsEditor';
import PreviewModal from './components/PreviewModal';

const AdminTermsManagement = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isSaving, setSaving] = useState(false);
  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTerm, setActiveTerm] = useState(null);

  const [termsContent, setTermsContent] = useState('');

  const metadata = useMemo(() => {
    const createdAt = activeTerm?.created_at ? new Date(activeTerm.created_at) : null;
    return {
      title: activeTerm?.title || '서비스 이용약관',
      version: activeTerm?.version || '-',
      effectiveDate:
        createdAt && !Number.isNaN(createdAt.getTime())
          ? createdAt.toISOString().slice(0, 10)
          : new Date()?.toISOString()?.split('T')?.[0],
    };
  }, [activeTerm]);


  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin-login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;

    let mounted = true;

    const run = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const { data, error } = await supabase
          .from('terms')
          .select('id, title, content, version, is_active, created_at')
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) throw error;
        const row = data?.[0];
        if (!row) {
          throw new Error('is_active=true 인 약관이 없습니다.');
        }

        if (!mounted) return;
        setActiveTerm(row);
        setTermsContent(row?.content || '');
      } catch (e) {
        if (!mounted) return;
        setActiveTerm(null);
        setTermsContent('');
        setErrorMessage(e?.message || '약관 데이터를 불러오지 못했습니다.');
      } finally {
        if (!mounted) return;
        setIsLoading(false);
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  const handleSave = async () => {
    if (!activeTerm?.id) return;

    setSaving(true);
    setErrorMessage('');

    try {
      const { data, error } = await supabase
        .from('terms')
        .update({
          content: termsContent,
          is_active: true,
        })
        .eq('id', activeTerm.id)
        .select('id, title, content, version, is_active, created_at')
        .single();

      if (error) throw error;

      setActiveTerm(data);
      setTermsContent(data?.content || '');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (e) {
      setErrorMessage(e?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };


  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      <NavigationBreadcrumbs />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {showSuccessMessage && (
          <div className="mb-6 rounded-lg bg-success/10 p-4">
            <div className="flex items-center gap-2">
              <Icon name="CheckCircle" size={20} className="text-success" />
              <p className="text-sm font-medium text-success">변경사항이 성공적으로 저장되었습니다.</p>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 rounded-lg bg-error/10 p-4">
            <div className="flex items-center gap-2">
              <Icon name="AlertTriangle" size={20} className="text-error" />
              <p className="text-sm font-medium text-error">{errorMessage}</p>
            </div>
          </div>
        )}

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">약관 관리</h1>
            <p className="mt-1 text-sm text-muted-foreground text-white">
              서비스 이용약관을 작성하고 관리하세요
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              iconName="Eye"
              iconPosition="left"
              onClick={() => setPreviewOpen(true)}
              className="bg-white hover:bg-white"
              disabled={isLoading || !activeTerm}
            >
              미리보기
            </Button>
            <Button
              variant="outline"
              iconName="Save"
              iconPosition="left"
              onClick={handleSave}
              loading={isSaving}
              className="bg-white hover:bg-white"
              disabled={isLoading || !activeTerm}
            >
              저장
            </Button>
          </div>
        </div>

        <div>
          <div>
            <TermsEditor
              content={termsContent}
              onChange={setTermsContent}
              onSave={handleSave}
              isSaving={isSaving}
            />
          </div>
        </div>
      </main>
      <PreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setPreviewOpen(false)}
        content={termsContent}
        metadata={metadata}
      />
    </div>
  );
};

export default AdminTermsManagement;